/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useEffect, useRef, useState} from 'react';
import InView from 'react-intersection-observer';
import Breadcrumbs from '@material-ui/core/Breadcrumbs';
import Link from '@material-ui/core/Link';
import {makeStyles} from '@material-ui/styles';
import classnames from 'classnames';
import isString from 'lodash.isstring';
import EntityLabel from '@ion/core/src/entities/EntityLabel';
import {translate} from '@ion/core/src/locale/locale';
import {Crumbs, SubAccountLink} from '@ion/core/src/types/entities';
import {FilteredAccounts} from '@ion/core/src/types/general';
import {useStyles as crumbStyles} from '@ion/dls/src/components/breadcrumbs/Breadcrumbs';
import {dlsColor} from '@ion/dls/src/theme/theme';
import GlobalGroupsSearch, {Page_LIMIT} from '@ion/features/src/global-groups/search';
import {toggleThemeStyle} from '../../theme/helpers';
import useThemeContext from '../../theme/useThemeContext';
import {DLSTheme} from '../../types/types';
import {IonSpinner} from '../loading/IonSpinner';
import GroupSelection from './GroupSelection';

const useStyles = makeStyles((theme: DLSTheme) => ({
  spinnerWrapper : {
    display        : 'flex',
    margin         : '0 auto',
    justifyContent : 'center'
  },
  hideSpinner : {
    display : 'none'
  },
  resultWrapper : toggleThemeStyle({
    padding   : '5px 16px 20px',
    borderTop : `solid 1px ${dlsColor('grey5')}`,
    '& div'   : {
      overflowY : 'auto',
      '& nav'   : {
        alignSelf : 'flex-start'
      }
    }
  }, {
    padding : '16px 16px',
    '& div' : {
      overflowY : 'auto',
      '& nav'   : {
        alignSelf : 'flex-start'
      }
    },
    '& .MuiBreadcrumbs-separator' : {
      bottom   : '2px',
      position : 'relative'
    },
    '& .MuiTypography-colorPrimary' : {
      color         : dlsColor('linkBlue'),
      fontSize      : '14px',
      fontFamily    : 'Roboto',
      fontWeight    : 600,
      lineHeight    : '16px',
      letterSpacing : '0.5px'
    }
  }),
  label : toggleThemeStyle({
    '& .MuiFormControlLabel-root' : {
      height : '45px'
    }
  }, {
    maxWidth                      : '96%',
    '& .MuiFormControlLabel-root' : {
      width                 : '92%',
      height                : '32px',
      margin                : '20px',
      borderRadius          : '8px',
      '&:has(.Mui-checked)' : {
        backgroundColor : dlsColor('paleBlue')
      },
      '& .MuiTypography-body1' : {
        width : '100%'
      }
    },
    '& .MuiFormControlLabel-label' : {
      width : '96%'
    }
  }),
  parentLink : toggleThemeStyle({
    color          : dlsColor('grey3'),
    textDecoration : 'none'
  }, {
    color         : dlsColor('linkBlue'),
    fontSize      : '14px',
    fontFamily    : 'Roboto',
    fontWeight    : 500,
    lineHeight    : '16px',
    letterSpacing : '0.5px',
    textTransform : 'capitalize',
    '&:hover'     : {
      cursor : 'pointer'
    }
  }),
  loadMoreWrapper : {
    display        : 'flex',
    justifyContent : 'center'
  },
  noResults : toggleThemeStyle({
    padding                        : '55px 25px 25px 25px',
    textAlign                      : 'center',
    minHeight                      : 65,
    display                        : 'flex',
    justifyContent                 : 'center',
    alignItems                     : 'center',
    width                          : '100%',
    boxSizing                      : 'border-box',
    [theme.breakpoints.down('sm')] : {
      marginLeft : '0'
    }
  }, {
    color                          : dlsColor('grey1'),
    display                        : 'flex',
    padding                        : '55px 25px 25px 25px',
    fontSize                       : '16px',
    boxSizing                      : 'border-box',
    fontFamily                     : 'Inter',
    fontWeight                     : 600,
    lineHeight                     : '20px',
    letterSpacing                  : '0px',
    justifyContent                 : 'center',
    [theme.breakpoints.down('sm')] : {
      marginLeft : '0'
    }
  })
}));

interface GlobalGroupsQueryResultsItemsProps {
  searchValue: string;
  onViewSubaccounts: Function;
  handleCrumbClick: Function;
  hideParentAccount?: boolean;
  onContentScrollToTop?: Function;
}

interface BreadCrumbsProps {
  groupTitle: string;
  crumbs: Crumbs;
  baseUrl: string;
  parentLinkClass: string;
  handleCrumbClick: Function;
  isCurrentTheme?: boolean,
  allCrumbsClickable: boolean;
  subAccounts?: SubAccountLink[];
  hideParentAccount?: boolean;
}

const BreadCrumbs = ({
  groupTitle,
  crumbs,
  baseUrl,
  isCurrentTheme,
  hideParentAccount,
  parentLinkClass,
  handleCrumbClick,
  allCrumbsClickable,
  subAccounts
}: BreadCrumbsProps) => {
  const classes   = crumbStyles({height: hideParentAccount ? '45px' : '70px'});
  const newCrumbs = hideParentAccount ? (crumbs.path).slice(0, -1) : (crumbs.path);

  const crumbClick = useCallback((index, crumb: any) => {
    if (index !== (Object.keys(crumbs).length) - 1 || allCrumbsClickable) {
      handleCrumbClick({
        title : isCurrentTheme ? (isString(crumb) ? crumb : EntityLabel.get(crumb)) : groupTitle,
        href  : crumb?.href || baseUrl,
        subAccounts
      });
    }
  }, [
    crumbs,
    baseUrl,
    groupTitle,
    subAccounts,
    isCurrentTheme,
    allCrumbsClickable
  ]);

  return (
    <div className={classes.breadcrumbRoot}>
      <Breadcrumbs maxItems={3}
        aria-label='breadcrumb'
        itemsAfterCollapse={hideParentAccount ? 2 : 1}
        itemsBeforeCollapse={hideParentAccount ? 0 : 1}
        {...(isCurrentTheme && {
          separator : '>'
        })}>
        {newCrumbs.map((crumb: any, index: number, path: string[]) => (
          <Link underline={'none'}
            classes={{root: index < path.length - 1 ? parentLinkClass : null}}
            onClick={function() {
              crumbClick(index, crumb);
            }}>
            {isString(crumb) ? crumb : EntityLabel.get(crumb)}
          </Link>
        ))}
      </Breadcrumbs>
    </div>
  );
};

/**
 * Component to display Global Account Items with infinite scrolling.
 * ```typescript
 *  import GlobalGroupsQueryResultsItems from '@ion/dls/src/components/groupchooser/GlobalGroupsQueryResultsItems';
 *
 *  <GroupSelection searchValue={searchValue} onViewSubaccounts={onViewSubaccounts} />
 * ```
 */
const GlobalGroupsQueryResultsItems = ({
  searchValue,
  onViewSubaccounts,
  handleCrumbClick,
  onContentScrollToTop,
  hideParentAccount = false
}: GlobalGroupsQueryResultsItemsProps) => {
  const classes          = useStyles();
  const loaderRef        = useRef();
  const totalSearchCount = GlobalGroupsSearch.fetchResultsCount();
  const [page, setPage]  = useState<number>(1);

  const {
    isCurrent : isCurrentTheme
  } = useThemeContext();

  const handleInViewChange = useCallback(function(isVisible: boolean) {
    if (isVisible) {
      setPage(page + 1);
    }
  }, [page]);

  useEffect(function() {
    if (page === 1) {
      onContentScrollToTop?.();
    }
  }, [page]);

  return (
    <>
      {!GlobalGroupsSearch.fetchResultsCount() && (
        <div>
          <span className={classes.noResults}>{translate('common:search.noResultsTextDefault')}</span>
        </div>
      )}
      {GlobalGroupsSearch.fetchResults(page).map(({item: group}: FilteredAccounts, index: number) => (
        <div key={`${group.href}-${index}`}
          className={classnames(
            classes.resultWrapper,
            hideParentAccount ? classes.label : isCurrentTheme ? classes.label : ''
          )}>
          <BreadCrumbs crumbs={group}
            groupTitle={group.title}
            isCurrentTheme={isCurrentTheme}
            subAccounts={group.subAccounts}
            handleCrumbClick={handleCrumbClick}
            hideParentAccount={hideParentAccount}
            baseUrl={group.href}
            allCrumbsClickable={true}
            parentLinkClass={classes.parentLink}/>
          <GroupSelection item={group}
            searchValue={searchValue}
            onViewSubaccounts={onViewSubaccounts}/>
        </div>
      ))}
      {totalSearchCount > page * Page_LIMIT + 1 && (
        <InView onChange={handleInViewChange}>
          <div ref={loaderRef} className={classes.spinnerWrapper}>
            <IonSpinner />
          </div>
        </InView>)}
    </>
  );
};

export default GlobalGroupsQueryResultsItems;
